import { normalize } from 'normalizr'
import { Schemas } from 'redux-modules/schema'
import { updateEntities } from 'redux-modules/entity'
import api from 'api'

import { addImage, deleteImage } from './image'

const FETCH_REQUEST = 'realhub/user/FETCH_REQUEST'
const FETCH_SUCCESS = 'realhub/user/FETCH_SUCCESS'
const FETCH_FAILURE = 'realhub/user/FETCH_FAILURE'

const CREATE_REQUEST = 'realhub/user/CREATE_REQUEST'
const CREATE_SUCCESS = 'realhub/user/CREATE_SUCCESS'
const CREATE_FAILURE = 'realhub/user/CREATE_FAILURE'

const UPDATE_REQUEST = 'realhub/user/UPDATE_REQUEST'
const UPDATE_SUCCESS = 'realhub/user/UPDATE_SUCCESS'
const UPDATE_FAILURE = 'realhub/user/UPDATE_FAILURE'

const SELECT_USER = 'realhub/user/SELECT_USER'

// Initial State
const initialState = {
  result: [],
  selectedId: null,
  loadedIds: [],
  loadedUsersForEntities: [],
  selected: {},
  loaded: false,
  loading: false,
  updating: false,
  creating: false,
  errors: [],
}

// Actions
export function fetchRequest(payload = {}){
  return {
    type: FETCH_REQUEST,
    loading: true,
    loadedIds: payload.loadedIds,
    loadedUsersForEntities: payload.loadedUsersForEntities,
  }
}

export function fetchSuccess(payload = {}){
  return {
    type: FETCH_SUCCESS,
    loading: false,
    loaded: true,
    errors: [],
    result: payload.result,
  }
}

export function fetchFailure(errors = []){
  return {
    type: FETCH_FAILURE,
    loaded: false,
    loading: false,
    errors,
  }
}

export function createRequest(){
  return {
    type: CREATE_REQUEST,
  }
}

export function createSuccess(){
  return {
    type: CREATE_SUCCESS,
  }
}

export function createFailure(errors = []){
  return {
    type: CREATE_FAILURE,
    errors,
  }
}

export function updateRequest(){
  return {
    type: UPDATE_REQUEST,
  }
}

export function updateSuccess(){
  return {
    type: UPDATE_SUCCESS,
  }
}

export function updateFailure(errors = []){
  return {
    type: UPDATE_FAILURE,
    errors,
  }
}

export function selectUserId(userId){
  return (dispatch) => {
    dispatch({
      type: SELECT_USER,
      selectedId: userId,
    })
  }
}

// Helper Functions
const handleError = errors => errors || ['Something went wrong']

// Action Creators
export function searchUsers(options, callback){
  return (dispatch) => {
    // Set Loading
    dispatch(fetchRequest())

    const promise = api('/admin/users/search.json', options)
      .then(response => response.json().then(json => ({ json, response })))
      .then(({ json, response }) => {
        if (!response.ok){
          return Promise.reject(json)
        }

        const normalizedJson = normalize(json, Schemas.USER_ARRAY)
        dispatch(updateEntities(normalizedJson))
        dispatch(fetchSuccess(normalizedJson))

        if (callback) callback(json)

        return json
      })
      .catch((json) => {
        const errors = handleError(json.errors)
        dispatch(fetchFailure(errors))
      })

    return promise
  }
}

export function loadUsers(entityType, entityId, options){
  const entityKey = `${entityType}${entityId}`
  return (dispatch, getState) => {
    const loadedUsersForEntities = getState().users.loadedUsersForEntities
      ? getState().users.loadedUsersForEntities.slice()
      : []

    if (!loadedUsersForEntities.includes(entityKey)){
      loadedUsersForEntities.push(entityKey)
    }

    // Set Loading
    dispatch(fetchRequest({ loadedUsersForEntities }))

    const { url, ...requestOptions } = options

    const promise = api(url || '/admin/users.json', requestOptions)
      .then(response => response.json().then(json => ({ json, response })))
      .then(({ json, response }) => {
        if (!response.ok){
          return Promise.reject(json)
        }

        const normalizedJson = normalize(json, Schemas.USER_ARRAY)
        dispatch(updateEntities(normalizedJson))
        dispatch(fetchSuccess(normalizedJson))

        return json
      })
      .catch((json) => {
        const errors = handleError(json.errors)
        dispatch(fetchFailure(errors))
      })

    return promise
  }
}

export function loadUser(user, options){
  return (dispatch, getState) => {
    const state = getState()
    const loadedIds = state.users.loadedIds ? state.users.loadedIds.slice() : []
    if (!loadedIds.includes(user.id)){
      loadedIds.push(user.id)
    }

    dispatch(fetchRequest({ loadedIds }))

    const promise = api(`/admin/users/${user.id}.json`, options)
      .then(response => response.json().then(json => ({ json, response })))
      .then(({ json, response }) => {
        if (!response.ok){
          return Promise.reject(json)
        }

        const normalizedJson = normalize(json, Schemas.USER)
        dispatch(updateEntities(normalizedJson))
        dispatch(fetchSuccess())

        return json
      })
      .catch((json) => {
        const errors = handleError(json.errors)
        dispatch(fetchFailure(errors))
      })

    return promise
  }
}

export function createUser(user, options, callback, errorCallback){
  const config = {
    method: 'POST',
    body: JSON.stringify({
      user,
    }),
  }

  return (dispatch) => {
    // Set Loading
    dispatch(createRequest())

    const promise = api('/admin/users.json', options, config)
      .then(response => response.json().then(json => ({ json, response })))
      .then(({ json, response }) => {
        if (!response.ok){
          return Promise.reject(json)
        }

        const normalizedJson = normalize(json, Schemas.USER)
        dispatch(updateEntities(normalizedJson))
        dispatch(createSuccess())

        if (callback) callback(json)

        return json
      })
      .catch((json) => {
        const errors = handleError(json.errors)
        dispatch(createFailure(errors))

        if (errorCallback) errorCallback(json)
      })

    return promise
  }
}

export function updateUser(user, options, callback){
  const config = {
    method: 'PUT',
    body: JSON.stringify({
      user,
    }),
  }

  return (dispatch) => {
    // Set Loading
    dispatch(updateRequest())

    const promise = api(`/admin/users/${user.id}.json`, options, config)
      .then(response => response.json().then(json => ({ json, response })))
      .then(({ json, response }) => {
        if (!response.ok){
          return Promise.reject(json)
        }

        const normalizedJson = normalize(json, Schemas.USER)
        dispatch(updateEntities(normalizedJson))
        dispatch(updateSuccess())

        if (callback) callback(json)

        return { success: true, result: json }
      })
      .catch((json) => {
        const errors = handleError(json.errors)
        dispatch(updateFailure(errors))

        return { success: false, result: errors }
      })

    return promise
  }
}

export function generateUserAuthenticationToken(user, options, callback){
  const config = {
    method: 'PUT',
  }

  return (dispatch) => {
    // Set Loading
    dispatch(updateRequest())

    const promise = api(`/admin/users/${user.id}/generate_auth_token.json`, options, config)
      .then(response => response.json().then(json => ({ json, response })))
      .then(({ json, response }) => {
        if (!response.ok){
          return Promise.reject(json)
        }

        const normalizedJson = normalize(json, Schemas.USER)
        dispatch(updateEntities(normalizedJson))
        dispatch(updateSuccess())

        if (callback) callback(json)

        return json
      })
      .catch((json) => {
        const errors = handleError(json.errors)
        dispatch(updateFailure(errors))
      })

    return promise
  }
}

export function updateTwoFactorMethod(user, options, callback){
  const config = {
    method: 'PUT',
  }

  return (dispatch) => {
    // Set Loading
    dispatch(updateRequest())

    const promise = api(`/admin/users/${user.id}/update_two_factor_method.json`, options, config)
      .then(response => response.json().then(json => ({ json, response })))
      .then(({ json, response }) => {
        if (!response.ok){
          return Promise.reject(json)
        }

        const normalizedJson = normalize(json, Schemas.USER)
        dispatch(updateEntities(normalizedJson))
        dispatch(updateSuccess())

        if (callback) callback(json)

        return json
      })
      .catch((json) => {
        const errors = handleError(json.errors)
        dispatch(updateFailure(errors))
      })

    return promise
  }
}

export function addUserImage(image = {}){
  return (dispatch, getState) => {
    const state = getState()

    // Add the image
    dispatch(addImage(image))

    // Add the image ID to the user
    const user = {
      ...state.entities.users[image.imageable_id],
    }
    user.images.push(image.id)

    const normalizedJson = normalize(user, Schemas.USER)
    dispatch(updateEntities(normalizedJson))
  }
}

export function deleteUserImage(user, image = {}){
  return (dispatch, getState) => {
    const state = getState()

    // Remove the image from the user
    const userEntity = {
      ...state.entities.users[image.imageable_id],
    }
    userEntity.images.some((imageId, index) => {
      if (imageId === image.id){
        userEntity.images.splice(index, 1)
        return true
      }
      return false
    })

    // Delete the image
    dispatch(deleteImage(image))

    const normalizedJson = normalize(userEntity, Schemas.USER)
    dispatch(updateEntities(normalizedJson))
  }
}

export function importUsersCsv(file, email, options){
  const config = {
    method: 'POST',
    body: JSON.stringify({
      file,
      email,
    }),
  }

  return (dispatch) => {
    dispatch(createRequest())

    const promise = api('/admin/users/import_users_csv.json', options, config)
      .then(response => response.json().then(json => ({ json, response })))
      .then(({ json, response }) => {
        if (!response.ok){
          return Promise.reject(json)
        }

        dispatch(createSuccess())

        return { success: true, result: json }
      })
      .catch((json) => {
        const errors = handleError(json.errors)
        dispatch(createFailure(errors))

        return { success: false, result: errors }
      })

    return promise
  }
}

// Reducer
export default function reducer(state = initialState, action = {}){
  switch (action.type){
    case FETCH_REQUEST:
      return {
        ...state,
        loadedIds: action.loadedIds || state.loadedIds,
        loadedUsersForEntities: action.loadedUsersForEntities || state.loadedUsersForEntities,
        loading: action.loading,
      }
    case FETCH_SUCCESS:
      return {
        ...state,
        errors: [],
        loaded: action.loaded,
        loading: action.loading,
        result: action.result,
      }
    case FETCH_FAILURE:
      return {
        ...state,
        errors: action.errors,
        loaded: action.loaded,
        loading: action.loading,
      }
    case CREATE_REQUEST:
      return {
        ...state,
        creating: true,
      }
    case CREATE_SUCCESS:
      return {
        ...state,
        creating: false,
        errors: [],
      }
    case CREATE_FAILURE:
      return {
        ...state,
        creating: false,
        errors: action.errors,
      }
    case UPDATE_REQUEST:
      return {
        ...state,
        updating: true,
      }
    case UPDATE_SUCCESS:
      return {
        ...state,
        errors: [],
        updating: false,
      }
    case UPDATE_FAILURE:
      return {
        ...state,
        errors: action.errors,
        updating: false,
      }
    case SELECT_USER:
      return {
        ...state,
        selectedId: action.selectedId,
      }
    default:
      return state
  }
}
